import React from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredTooltip } from '../../hooks/useAnchoredTooltip';
import type { TooltipPosition } from './tooltipPlacement';

export type { TooltipPosition } from './tooltipPlacement';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: TooltipPosition;
  /** Hover/focus delay before show. Default 700ms (SaaS: avoid flicker). */
  delay?: number;
  disabled?: boolean;
  /**
   * Wrapper around the trigger. Default `contents` for flex layouts.
   * Use e.g. `flex min-w-0 flex-1` when `contents` breaks hover (common in some browsers).
   */
  wrapperClassName?: string;
  /** Follow the mouse cursor instead of anchoring to the trigger. */
  followCursor?: boolean;
}

/**
 * Action / label tooltip (SaaS defaults):
 * - Anchored to the trigger element by default (or cursor when `followCursor`)
 * - Default below (`bottom`), auto-flips near viewport edges (anchored mode)
 * - Delayed show; hidden on click / Escape / leave
 * - No hover on touch devices
 * - Portal + pointer-events-none so it never steals the cursor
 */
const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'bottom',
  delay = 700,
  disabled = false,
  wrapperClassName = 'contents',
  followCursor = false,
}) => {
  const {
    isVisible,
    coords,
    tooltipId,
    wrapperRef,
    isTouch,
    shouldShowOnHover,
    showTooltip,
    hideTooltip,
    handleTriggerPointer,
    handleMouseMove,
    handleMouseEnter,
    bubbleStyle,
  } = useAnchoredTooltip({
    position,
    delay,
    disabled: disabled || !content,
    touchBehavior: 'off',
    followCursor,
  });

  return (
    <>
      <div
        ref={wrapperRef}
        className={wrapperClassName}
        onMouseEnter={
          shouldShowOnHover ? (handleMouseEnter ?? showTooltip) : undefined
        }
        onMouseLeave={shouldShowOnHover ? hideTooltip : undefined}
        onMouseMove={shouldShowOnHover ? handleMouseMove : undefined}
        onFocus={shouldShowOnHover ? showTooltip : undefined}
        onBlur={shouldShowOnHover ? hideTooltip : undefined}
        onMouseDown={handleTriggerPointer}
        onClick={handleTriggerPointer}
        onTouchStart={isTouch ? handleTriggerPointer : undefined}
        aria-describedby={isVisible ? tooltipId : undefined}
      >
        {children}
      </div>

      {isVisible && coords && bubbleStyle && typeof document !== 'undefined' &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[3000] px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap pointer-events-none"
            style={bubbleStyle}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
